import { useState, type FormEvent } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { authErrorMessage, signIn, signInComEmail, useSession } from "@/lib/auth";
import { configured } from "@/lib/firebase";
import { ErrorNote, Spinner } from "@/ui/primitives";

/**
 * Entrada.
 *
 * Diz o que o app é ANTES de pedir login. No app anterior a pessoa
 * precisava entrar com a conta do Google para descobrir do que se tratava —
 * barreira gratuita para quem chega pela loja sem conhecer ninguém.
 *
 * O Google é a porta de entrada; a de e-mail e senha fica dobrada,
 * embaixo. Ela existe para quem recebeu uma senha da gente — hoje, quem
 * revisa o app na Google Play. Fica visível, e não escondida atrás de um
 * toque secreto, porque app que se comporta diferente na revisão é o que
 * a política proíbe.
 */
export function SignIn() {
  const { user, loading } = useSession();
  const location = useLocation();
  const [erro, setErro] = useState("");
  const [entrando, setEntrando] = useState(false);
  const [comEmail, setComEmail] = useState(false);
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");

  const destino = (location.state as { from?: string } | null)?.from ?? "/";

  if (loading) return <div className="screen center"><Spinner /></div>;
  if (user) return <Navigate to={destino} replace />;

  async function entrar() {
    setErro("");
    setEntrando(true);
    try {
      await signIn();
    } catch (err) {
      setEntrando(false);
      setErro(authErrorMessage(err));
    }
  }

  async function entrarComEmail(e: FormEvent) {
    e.preventDefault();
    setErro("");
    setEntrando(true);
    try {
      await signInComEmail(email, senha);
    } catch (err) {
      setEntrando(false);
      setErro(authErrorMessage(err));
    }
  }

  return (
    <main className="screen center pad">
      <div className="hero">
        <div className="hero-mark" aria-hidden="true">🍜</div>
        <h1>Nhomi</h1>
        <p>Todo dia, todo mundo posta o prato.<br />Quem vacilar aparece no fim da tabela.</p>
      </div>

      <ul className="pitch">
        <li><span aria-hidden="true">📸</span> Um prato por dia, foto obrigatória</li>
        <li><span aria-hidden="true">⭐</span> A galera dá nota — anônima</li>
        <li><span aria-hidden="true">🎯</span> Chute o preço e as calorias e ganhe ponto</li>
        <li><span aria-hidden="true">🏆</span> Placar semanal pra decidir quem pagou de vacilão</li>
      </ul>

      {erro ? <ErrorNote>{erro}</ErrorNote> : null}

      {configured ? (
        <>
          <button className="btn btn-google" onClick={entrar} disabled={entrando}>
            <GoogleMark />
            {entrando ? "Entrando…" : "Entrar com Google"}
          </button>
          <p className="fine">A gente usa só seu nome e sua foto do Google.</p>

          {comEmail ? (
            <form className="entrar-email" onSubmit={entrarComEmail}>
              <div className="card">
                <label className="field">
                  <span className="field-label">E-mail</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(ev) => setEmail(ev.target.value)}
                    autoComplete="username"
                    autoCapitalize="none"
                    spellCheck={false}
                    required
                  />
                </label>
                <label className="field">
                  <span className="field-label">Senha</span>
                  <input
                    type="password"
                    value={senha}
                    onChange={(ev) => setSenha(ev.target.value)}
                    autoComplete="current-password"
                    required
                  />
                </label>
              </div>
              <button className="btn btn-outline" type="submit" disabled={entrando}>
                {entrando ? "Entrando…" : "Entrar"}
              </button>
              <p className="fine center">
                Só para quem recebeu uma senha da gente. Pra entrar no seu
                grupo, use o Google aí em cima.
              </p>
            </form>
          ) : (
            <button className="btn btn-ghost" onClick={() => setComEmail(true)}>
              Entrar com e-mail e senha
            </button>
          )}
        </>
      ) : (
        /* Sem as chaves não dá pra entrar, e o recado é para QUEM ESTÁ
           OLHANDO. Antes esta linha mandava copiar o `.env.example` — só
           que quem lê a tela de entrar é quem baixou o app, não quem o
           compila, e essa é a primeira tela que a revisão da Play abre.
           Quem trabalha no projeto descobre o mesmo pelo console. */
        <ErrorNote>
          O app não conseguiu iniciar agora. Fecha e abre de novo — se
          continuar, avisa a gente em nhomi.principal@nhomi.app.
        </ErrorNote>
      )}
    </main>
  );
}

function GoogleMark() {
  return (
    <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.6 2.6 30.2.5 24 .5 14.6.5 6.5 5.9 2.6 13.7l7.8 6.1C12.3 13.7 17.6 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.2-.4-4.7H24v9h12.7c-.6 3-2.3 5.5-4.8 7.2l7.5 5.8c4.4-4 7.1-10 7.1-17.3z" />
      <path fill="#FBBC05" d="M10.4 28.2a14.6 14.6 0 0 1 0-8.4l-7.8-6.1a23.9 23.9 0 0 0 0 20.6l7.8-6.1z" />
      <path fill="#34A853" d="M24 47.5c6.2 0 11.5-2 15.4-5.6l-7.5-5.8c-2.1 1.4-4.8 2.2-7.9 2.2-6.4 0-11.7-4.2-13.6-9.9l-7.8 6.1C6.5 42.1 14.6 47.5 24 47.5z" />
    </svg>
  );
}
